import { useState, useEffect, forwardRef } from "react";
import type { UniqueIdentifier, DragEndEvent } from "@dnd-kit/core";
import {
  DndContext,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import { ChevronUp, GripDotsVertical } from "@unified-checkout/design-system/icons/index";
import { Toggle } from "@unified-checkout/design-system";

import { useDndKitProps } from "@app/dashboard/hooks/dnd_kit/useDndKitProps";
import { useDndKitSensors } from "@app/dashboard/hooks/dnd_kit/useDndKitSensors";
import type { IDraggableCard, IDraggableCardBase } from "@app/dashboard/types/innerLayout/draggable-card";
import styles from "./draggable-card.module.scss";


export const DraggableCard = forwardRef<HTMLDivElement, IDraggableCard>((props, ref) => {

  const {
    id,
    collapsible,
    collapsed: collapsedDefaultValue,
    childrenProps,
    onSwap,
    onCollapse,
    orderBy
  } = props;

  const [collapsed, setCollapsed] = useState(!!collapsedDefaultValue);
  const [sortableChildren, setSortableChildren] = useState(collapsible ? childrenProps : undefined);

  useEffect(() => {
    setSortableChildren(childrenProps);
  }, [childrenProps]);

  useEffect(() => {
    setCollapsed(!!collapsedDefaultValue);
  }, [collapsedDefaultValue]);

  const DraggableCardBase = ({
    id,
    label,
    value,
    icon,
    active,
    draggable,
    collapsible,
    onChangeHandler,
    onBeforeToggleConfirmation,
    children,
    ref: cardRef,
    isChildCard
  }: IDraggableCardBase) => {

    const {
      attributes,
      listeners,
      style,
      setNodeRef
    } = useDndKitProps({ id: id as UniqueIdentifier });

    return (
      <div
        className={styles["draggable-card"]}
        style={style}
        ref={setNodeRef}
        data-child-draggable-card={!!isChildCard}
      >

        <div className={styles["draggable-card__info-box"]}>
          {
            icon &&
            <div className={styles["draggable-card__info-box__icon"]}>
              {typeof icon === 'string' ? <img src={icon} alt={label} /> : icon}
            </div>
          }

          <div className={styles["draggable-card__info-box__label"]}>{label}</div>

        </div>

        <div className={styles["draggable-card__actions-box"]}>
          {
            collapsible ?
              <div
                className={styles["draggable-card__actions-box__collapse-icon"]}
                data-draggable-card-collapsed={collapsed}
                onClick={() => {
                  setCollapsed((prev) => {
                    const newCollapseState = !prev;

                    onCollapse?.(id, newCollapseState);
                    return newCollapseState
                  });
                }}
              >
                {ChevronUp}
              </div>
              :
              <Toggle
                size="sm"
                checked={active}
                onBeforeToggleConfirmation={(checked) => onBeforeToggleConfirmation?.(checked, id) as any}
                onChangeHandler={() => {
                  onChangeHandler?.(!!active, value);
                }}
              />
          }

          {
            draggable &&
            <div
              className={styles["draggable-card__actions-box__drag-icon"]}
              ref={cardRef || setNodeRef}
              {...attributes}
              {...listeners}
            >
              {GripDotsVertical}
            </div>
          }

        </div>

        {
          children && !collapsed &&
          <div className={styles["draggable-card__child"]}>
            {children}
          </div>
        }

      </div>
    );
  };

  const CollapsibleDraggableCard = (cardProps: IDraggableCard) => {

    const getCardPosition = (id: string | UniqueIdentifier) => {
      return sortableChildren?.findIndex(card => card.id === id);
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id === over?.id) return;

      setSortableChildren((card) => {
        const originalPosition = getCardPosition(active.id) || 0;
        const newPosition = getCardPosition(over?.id || '') || 0;

        const newSorting =
          card &&
          arrayMove(card, originalPosition, newPosition)
            .map((m, index) => ({ ...m, apiData: { ...m.apiData, [orderBy]: index } }));

        onSwap?.(id, newSorting as IDraggableCardBase[]);

        return newSorting;
      });
    };

    const sensors = useDndKitSensors();

    return (
      <DraggableCardBase
        {...cardProps}
        ref={ref}
      >
        {
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCorners}
            key={`dnd-context-${cardProps.id}`}
          >
            <SortableContext
              items={sortableChildren as ({ id: UniqueIdentifier })[]}
              strategy={verticalListSortingStrategy}
              key={`sortable-context-${cardProps.id}`}
            >
              {
                sortableChildren?.sort((a, b) => (a.apiData[orderBy] || 0) - (b.apiData[orderBy] || 0)).map((card, index) =>
                  <DraggableCardBase
                    key={`draggable-card-child-${card.id}-${index}`}
                    {...card}
                    onBeforeToggleConfirmation={(checked) => cardProps.onBeforeToggleConfirmation?.(checked, card.id) as any}
                    isChildCard={true}
                  />
                )
              }
            </SortableContext>
          </DndContext>
        }
      </DraggableCardBase>
    );
  };

  return (
    !!sortableChildren?.length ?
      <CollapsibleDraggableCard {...props} />
      :
      <DraggableCardBase {...props} ref={ref} />
  );
});